import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useCanvasStore } from '@/modules/canvas/store';
import {
  IProjectServiceConnector,
  ProjectServiceConnector,
} from '@/modules/api/ProjectApiConnector';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { useAppMetaStore } from '@/modules/floorplanV2/store/project/projectState';
import { usePointCloudUIStore } from '@/modules/pointCloud/store';
import { useCollisionStore } from '@/modules/collision/store/useCollisionStore';

import { FddmServiceImpl } from './FddmServiceImpl';
import { IFddmService } from './IFddmService';
import { FddmServiceLogDecorator } from './FddmServiceLogDecorator';
import { FddmServiceExceptionDecorator } from './FddmServiceExceptionDecorator';
import { FddmServiceHistoryDecorator } from './FddmServiceHistoryDecorator';
import { FddmServiceAutosaveDecorator } from './FddmServiceAutosaveDecorator';

export class FddmServiceFactory {
  private static instance: FddmServiceFactory | null = null;
  private floorplanDesignStore: typeof useFloorplanDesignStore;
  private canvasStore: typeof useCanvasStore;
  private appStore: typeof useAppStore;
  private appMetaStore: typeof useAppMetaStore;
  private pointCloudUIStore: typeof usePointCloudUIStore;
  private collisionStore: typeof useCollisionStore;
  private projectServiceConnector: IProjectServiceConnector;

  public static initialize(
    floorplanDesignStore: typeof useFloorplanDesignStore,
    canvasStore: typeof useCanvasStore,
    appStore: typeof useAppStore,
    appMetaStore: typeof useAppMetaStore,
    pointCloudUIStore: typeof usePointCloudUIStore,
    collisionStore: typeof useCollisionStore,
  ) {
    FddmServiceFactory.instance = new FddmServiceFactory(
      floorplanDesignStore,
      canvasStore,
      appStore,
      appMetaStore,
      pointCloudUIStore,
      collisionStore,
    );
  }

  public static getInstance() {
    if (FddmServiceFactory.instance === null) {
      throw new Error(
        'FddmServiceFactory not yet initialized. Call FddmServiceFactory.initialize() first.',
      );
    }
    return FddmServiceFactory.instance;
  }

  private constructor(
    floorplanDesignStore: typeof useFloorplanDesignStore,
    canvasStore: typeof useCanvasStore,
    appStore: typeof useAppStore,
    appMetaStore: typeof useAppMetaStore,
    pointCloudUIStore: typeof usePointCloudUIStore,
    collisionStore: typeof useCollisionStore,
  ) {
    this.floorplanDesignStore = floorplanDesignStore;
    this.canvasStore = canvasStore;
    this.appStore = appStore;
    this.appMetaStore = appMetaStore;
    this.pointCloudUIStore = pointCloudUIStore;
    this.collisionStore = collisionStore;
    this.projectServiceConnector = ProjectServiceConnector.getInstance();
  }

  public createFddmService(): IFddmService {
    let result: IFddmService = this.makeFddmService();

    result = new FddmServiceExceptionDecorator(result);
    result = new FddmServiceLogDecorator(result);
    result = new FddmServiceHistoryDecorator(
      result,
      this.floorplanDesignStore,
      this.pointCloudUIStore,
    );
    result = new FddmServiceAutosaveDecorator(
      result,
      this.appStore,
      this.appMetaStore,
      this.canvasStore,
      this.floorplanDesignStore,
      this.projectServiceConnector,
    );

    return result;
  }

  private makeFddmService(): IFddmService {
    return new FddmServiceImpl(
      this.floorplanDesignStore,
      this.pointCloudUIStore,
      this.collisionStore,
    );
  }
}
